// pages/people/staff/View.tsx
import { useStaffByIdQuery, useDeleteStaffMutation } from "@/hooks/api/staff.queries";
import { Spinner, ErrorMessage } from "@/components";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import StaffDialog from "@/components/formDialog/StaffDialog";
import { CustomAlertDialog } from "@/components";
import { ArrowLeft, Mail, Phone, Calendar, Shield } from "lucide-react";

const StaffView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: staff, isLoading, isError, refetch } = useStaffByIdQuery(id ?? "");

  const user = useAuthStore((state) => state.user);
  const canUpdate = user?.permissions?.includes("staff.update") || true; // Fallback or permission check
  const canDelete = user?.permissions?.includes("staff.delete") || true;

  const deleteMutation = useDeleteStaffMutation({});
  const deleteDialog = useAlertDialog();

  const staffDialog = useFormDialog<{
    mode: "create" | "edit";
    id: string;
  }>({ mode: "create", id: "" });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (isError || !staff) {
    return (
      <ErrorMessage
        message="Failed to load staff details."
        onRetry={() => refetch()}
      />
    );
  }

  const handleDelete = () => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate("/people/staff"),
      onSettled: () => deleteDialog.close(),
    });
  };

  const initials = (staff.name || "U")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const designationLabel = staff.designation_name
    ? staff.designation_name
        .split("_")
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : null;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6">
      {/* Top Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Button
          variant="ghost"
          className="w-fit gap-2 pl-0 hover:bg-transparent hover:text-gray-900 font-medium text-gray-700"
          onClick={() => navigate("/people/staff")}
        >
          <ArrowLeft size={16} />
          Back to Staff List
        </Button>
        <div className="flex gap-3">
          {canUpdate && (
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-800 hover:bg-gray-50"
              onClick={() => staffDialog.open({ mode: "edit", id: staff.id })}
            >
              Edit Staff
            </Button>
          )}
          {canDelete && (
            <Button
              variant="default"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                deleteDialog.open({
                  action: "delete",
                  mantraId: staff.id,
                  mantraTitle: staff.name,
                  message: (
                    <span>
                      Are you sure you want to deactivate{" "}
                      <strong className="text-gray-800">{staff.name}</strong>?
                    </span>
                  ),
                });
              }}
            >
              Deactivate
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Profile Card (Takes up 2 columns on large screens) */}
        <Card className="lg:col-span-2 border-gray-100 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-8">
            <div className="flex flex-col sm:flex-row gap-8 items-start">
              {/* Avatar */}
              <Avatar className="h-24 w-24 shrink-0 rounded-full border-4 border-white shadow-sm bg-blue-600">
                <AvatarFallback className="bg-blue-600 text-white text-3xl font-bold">
                  {initials}
                </AvatarFallback>
              </Avatar>

              {/* Info section */}
              <div className="flex-1 space-y-5">
                {/* Header (Name + Badges) */}
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{staff.name}</h1>
                  <Badge
                    variant={staff.is_active ? "default" : "secondary"}
                    className={
                      staff.is_active
                        ? "bg-gray-900 hover:bg-gray-800 text-white px-2.5 py-0.5 rounded-md font-medium"
                        : "bg-gray-200 text-gray-700 px-2.5 py-0.5 rounded-md font-medium"
                    }
                  >
                    {staff.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {designationLabel && (
                    <Badge variant="secondary" className="bg-gray-800 hover:bg-gray-700 text-white px-2.5 py-0.5 rounded-md font-medium">
                      {designationLabel}
                    </Badge>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 text-sm text-gray-500 font-medium">
                  <div className="flex items-center gap-2.5">
                    <Mail size={16} className="text-gray-400 shrink-0" />
                    <span className="truncate">{staff.email}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Phone size={16} className="text-gray-400 shrink-0" />
                    <span>{staff.phone}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Calendar size={16} className="text-gray-400 shrink-0" />
                    <span>Created: {new Date(staff.created_at).toISOString().split('T')[0]}</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Shield size={16} className="text-gray-400 shrink-0" />
                    <span>Last login: {staff.last_login_at ? new Date(staff.last_login_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : "Never"}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats Card (Takes up 1 column on large screens) */}
        <Card className="border-gray-100 shadow-sm rounded-xl overflow-hidden self-start">
          <CardHeader className="p-6 pb-4 border-b border-gray-50 bg-gray-50/50">
            <CardTitle className="text-base font-bold text-gray-900">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500 font-medium">Designation Rank</span>
              <span className="font-bold text-gray-900">{staff.designation_rank}</span>
            </div>
            
            <div className="h-px bg-gray-100 w-full my-1 rounded-full"></div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500 font-medium">Account Status</span>
              <Badge
                variant={staff.is_active ? "default" : "secondary"}
                className={
                  staff.is_active
                    ? "bg-gray-900/90 text-white rounded-md text-[10px] px-2 uppercase tracking-wide"
                    : "bg-gray-200 text-gray-700 rounded-md text-[10px] px-2 uppercase tracking-wide"
                }
              >
                {staff.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Reusable Dialogs */}
      <StaffDialog
        mode={staffDialog.state.mode as "create" | "edit"}
        isOpen={staffDialog.state.isOpen}
        onOpenChange={(open) => !open && staffDialog.close()}
        staffId={staffDialog.state.id}
        onCancel={staffDialog.close}
        filters={{}}
      />

      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deactivation"
        description={deleteDialog.state.message}
        confirmText="Yes, Deactivate"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </div>
  );
};

export default StaffView;
